import { Icon, Loader, Modal } from 'semantic-ui-react';
import { PropertyWithSearchesForContin, SearchForContin } from './types';
import { useEffect, useState } from 'react';
import { OrderContinForm } from './OrderContinForm';
import axios from 'axios';
import { useUserContext } from '../../../context/UserContext';
import produce from 'immer';
import {
  ActualContent,
  ButtonContainer,
  CancelButton,
  Close,
  ConfirmButton,
  ErrorText,
  Header,
  ModalContent,
  ModalHeader,
} from './styled';

interface PropTypes {
  open: boolean;
  properties: PropertyWithSearchesForContin[];
  onClose: () => void;
}

export const OrderContinModal = (props: PropTypes) => {
  const [loading, setLoading] = useState(false);
  const [selectedSearches, setSelectedSearches] = useState<SearchForContin[]>([]);
  const [emailAddressList, setEmailAddressList] = useState<string[]>(['', '']);
  const [defaultEmailAddresses, setDefaultEmailAddresses] = useState<string[]>([]);
  const [dueBy, setDueBy] = useState('');
  const [note, setNote] = useState('');
  const [success, setSuccess] = useState(false);
  const [hasSelectedSearchError, setHasSelectedSearchError] = useState(false);

  const { user } = useUserContext();

  useEffect(() => {
    if (user && props.properties[0]?.orderId) {
      getDefaultClientContactEmails();
    }
  }, [user]);

  const getDefaultClientContactEmails = async () => {
    var { data: emails } = await axios.get(
      `/api/clientSearch/getDefaultClientContactEmails/${user.clientId}/${props.properties[0].orderId}`
    );
    setDefaultEmailAddresses(emails);
  };

  const changeSearchSelection = (search: SearchForContin, checked: boolean) => {
    if (checked) {
      const newSelectedSearches = [...selectedSearches, search];
      setSelectedSearches(newSelectedSearches);
    } else {
      const newSelectedSearches = selectedSearches.filter(s => s.searchId !== search.searchId);
      setSelectedSearches(newSelectedSearches);
    }
  };

  const changeSearchSelectionBulk = (searches: SearchForContin[], checked: boolean) => {
    var newSelectedSearches = selectedSearches.filter(s => !searches.includes(s));
    if (checked) {
      newSelectedSearches = [...newSelectedSearches, ...searches];
    }
    setSelectedSearches(newSelectedSearches);
  };

  const changeEmailAddress = (value: string, index: number) => {
    const newEmailAddresses = produce(emailAddressList, draft => {
      draft[index] = value;
    });
    setEmailAddressList(newEmailAddresses);
  };

  const submitOrderContins = () => {
    setLoading(true);
    if (hasErrors()) {
      setLoading(false);
    } else {
      orderContins();
    }
  };

  const hasErrors = () => {
    const hasErrors = !(selectedSearches.length > 0);
    setHasSelectedSearchError(hasErrors);
    return hasErrors;
  };

  const orderContins = async () => {
    const propertyIdsWithSelectedSearches = new Set(selectedSearches.map(s => s.propertyId));

    const properties = getPropertiesForPostObj(propertyIdsWithSelectedSearches);
    const emailAddresses = emailAddressList.filter(e => e.length > 0);
    const userName = `${user.firstName || ''} ${user.lastName || ''}`;

    const objToPost = {
      properties,
      note,
      emailAddresses,
      dueBy,
      userName,
      clientName: user.clientName,
      userId: user.id,
      isContin: true,
    };
    await postAddContins(objToPost);
  };

  const getPropertiesForPostObj = (propertyIdsWithSelectedSearches: Set<number>) => {
    return props.properties
      .filter(p => propertyIdsWithSelectedSearches.has(p.propertyId))
      .map(p => {
        return {
          ...p,
          searchesForContin: selectedSearches
            .filter(s => s.propertyId == p.propertyId)
            .map(s => {
              return {
                ...s,
                searchType: {
                  id: s.searchTypeId,
                  type: `${s.searchType} ${s.searchType.includes('Tax') ? 'Contin' : 'Update'}`,
                  isSixMonthContin: s.sixMonthsSinceLastSearch,
                },
              };
            }),
        };
      });
  };

  const postAddContins = async (objToPost: any) => {
    await axios.post(`/api/clientSearch/AddContinsToOrder`, objToPost).then(() => {
      setLoading(false);
      setSuccess(true);
      setTimeout(() => {
        props.onClose();
      }, 2000);
    });
  };

  return (
    <Modal open={props.open} style={{ width: 860, zIndex: 500 }}>
      <ModalContent>
        <ModalHeader>
          <Header>{`Order Contins/Updated Searches for File ${
            props.properties[0]?.titleNumber ?? ''
          }`}</Header>
          <Close
            onClick={() => {
              props.onClose();
            }}
          >
            <Icon name="close" />
          </Close>
        </ModalHeader>
        <ActualContent>
          {props.properties.length > 0 ? (
            <OrderContinForm
              properties={props.properties}
              selectedSearches={selectedSearches}
              emailAddresses={emailAddressList}
              defaultEmailAddresses={defaultEmailAddresses}
              dueBy={dueBy}
              note={note}
              changeSearchSelection={changeSearchSelection}
              changeSearchSelectionBulk={changeSearchSelectionBulk}
              changeEmailAddress={changeEmailAddress}
              changeDueBy={setDueBy}
              changeNote={setNote}
            />
          ) : (
            `No completed searches on this order.`
          )}
        </ActualContent>
        <ButtonContainer>
          {hasSelectedSearchError && (
            <ErrorText>Please select at least one search before continuing.</ErrorText>
          )}
          <CancelButton
            onClick={() => {
              props.onClose();
            }}
          >
            Cancel
          </CancelButton>
          <ConfirmButton onClick={submitOrderContins}>
            {loading ? (
              <Loader style={{ margin: '0 auto' }} size="mini" inline="centered" />
            ) : success ? (
              'Success!'
            ) : (
              'Order'
            )}
          </ConfirmButton>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  );
};
